import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FiEdit2, FiMail } from "react-icons/fi";
import { toast } from "react-toastify";
import type { RootState } from "../../store/store";
import type { RateAdDto } from "../../api/rateAdApi";
import {
  cancelBookingByDatesApi,
  getBookingsByOwnerAdsApi,
  updateBookingReservationColorApi,
  updateManualBookingClientApi,
  validateBookingByDateApi,
} from "../../api/adsApi";
import {
  deleteCurrentUserMessageApi,
  listCurrentUserMessagesByInterlocutorApi,
  markCurrentUserConversationAsReadApi,
  sendCurrentUserMessageApi,
  sendCurrentUserTypingSignalApi,
} from "../../api/chatApi";
import { getLocalizedText } from "../../features/ads/i18nFields";
import type { Ad } from "../../features/ads/adsTypes";
import styles from "../MyAds/MyAds.module.css";
import ShimmerImage from "../../components/ShimmerImage/ShimmerImage";
import ChatMessageContent from "../../components/ChatMessageContent/ChatMessageContent";
import PhoneInput from "../../components/PhoneInputComponent/PhoneInput";
import { AD_IMAGE_PLACEHOLDER } from "../../utils/adImagePlaceholder";
import { getCountryValueDisplay, type CountryValue } from "../../features/country/countrySlice";
import { hasLeadingZeroPhoneDigit } from "../../utils/phoneValidation";

const MailIcon = FiMail as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;
const EditIcon = FiEdit2 as unknown as React.ComponentType<{ "aria-hidden"?: boolean; focusable?: string }>;

type ReservationDateItem = {
  date: string;
  booked: boolean | null;
  reservationColor?: string;
};

type ReservationGroupItem = {
  id: string;
  bookingId: string;
  bookingGroup: string;
  dates: ReservationDateItem[];
  reservationColor?: string;
  stayReview?: RateAdDto | null;
};

type ReservationUserItem = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
  nbrChild: number | null;
  country: CountryValue;
  indicatif: string;
  codeCountry: string;
};

type OwnerReservationItem = {
  id: string;
  ad: Ad;
  user: ReservationUserItem;
  groups: ReservationGroupItem[];
};

type ConfirmActionState = {
  mode: "validate" | "cancel" | "deletePending";
  reservationId: string;
  groupId: string;
};

type UiMessage = {
  id: string;
  content: string;
  isMine: boolean;
  createdAt: string;
  readAt?: string;
};

type ManualClientEditForm = {
  firstName: string;
  lastName: string;
  phone: string;
  status: string;
  nbr_child: number | "";
  country: string;
  indicatif: string;
  codeCountry: string;
};

type ManualClientEditErrors = Partial<Record<keyof ManualClientEditForm, string>>;

const placeholder = AD_IMAGE_PLACEHOLDER;
const RESERVATION_COLORS = [
  "#ef4444",
  "#f97316",
  "#f59e0b",
  "#eab308",
  "#84cc16",
  "#22c55e",
  "#10b981",
  "#14b8a6",
  "#06b6d4",
  "#0ea5e9",
  "#3b82f6",
  "#2563eb",
  "#6366f1",
  "#8b5cf6",
  "#a855f7",
  "#d946ef",
  "#c026d3",
  "#ec4899",
  "#f43f5e",
  "#64748b",
  "#111827",
];

const normalizeReservationColor = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return /^#[0-9A-Fa-f]{6}$/.test(trimmed) ? trimmed.toLowerCase() : undefined;
};

const getStableReservationColor = (seed: unknown): string => {
  const text = String(seed || "");
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return RESERVATION_COLORS[hash % RESERVATION_COLORS.length];
};

const getMessageTime = (value: string): number => {
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const isTemporaryMessageId = (value: string): boolean => /^\d{10,}-[a-z0-9]{6}$/i.test(String(value || "").trim());

const createClientMessageId = (): string => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const isRobotizedClientEmail = (value?: string): boolean =>
  /^client-[a-z0-9]+@internal\.local$/i.test(String(value || "").trim());

const normalizeFilterText = (value: unknown): string =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const normalizeFilterPhone = (value: unknown): string =>
  String(value || "").replace(/[^\d+]/g, "");

const normalizeUserStatus = (value: string): "single" | "married" | "married_with_children" | "" => {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "single" || normalized === "celibataire" || normalized === "célibataire") return "single";
  if (normalized === "married" || normalized === "marie_sans_enfants" || normalized === "marié") return "married";
  if (
    normalized === "married_with_children" ||
    normalized === "marie_avec_enfants" ||
    normalized === "marié avec enfants"
  ) {
    return "married_with_children";
  }
  return "";
};

const createEmptyManualClientEditForm = (): ManualClientEditForm => ({
  firstName: "",
  lastName: "",
  phone: "",
  status: "",
  nbr_child: "",
  country: "",
  indicatif: "",
  codeCountry: "",
});

const INLINE_CHAT_INPUT_MIN_HEIGHT = 30;
const INLINE_CHAT_INPUT_MAX_HEIGHT = 78;
const RESERVATIONS_PAGE_LIMIT = 20;

const resizeInlineChatInputElement = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = `${INLINE_CHAT_INPUT_MIN_HEIGHT}px`;
  textarea.style.height = `${Math.min(textarea.scrollHeight, INLINE_CHAT_INPUT_MAX_HEIGHT)}px`;
};

const upsertChatMessageById = (messages: UiMessage[], nextMessage: UiMessage): UiMessage[] => {
  const existingIndex = messages.findIndex((message) => message.id === nextMessage.id);
  if (existingIndex !== -1) {
    return messages
      .map((message, index) => (index === existingIndex ? { ...message, ...nextMessage } : message))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  const nextTime = getMessageTime(nextMessage.createdAt);
  const duplicateIndex = messages.findIndex((message) => {
    if (!nextMessage.isMine || !isTemporaryMessageId(message.id)) return false;
    if (message.isMine !== nextMessage.isMine) return false;
    if (message.content.trim() !== nextMessage.content.trim()) return false;

    const messageTime = getMessageTime(message.createdAt);
    if (!messageTime || !nextTime) return false;
    return Math.abs(messageTime - nextTime) <= 15000;
  });

  if (duplicateIndex !== -1) {
    return messages
      .map((message, index) => (index === duplicateIndex ? { ...message, ...nextMessage } : message))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  return [...messages, nextMessage].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
};

const getTypingUntilForInterlocutor = (typingByInterlocutor: Record<string, number>, interlocutorId: string): number => {
  const normalizedId = String(interlocutorId || "").trim();
  if (!normalizedId) return 0;

  const directTypingUntil = Number(typingByInterlocutor[normalizedId] || 0);
  if (directTypingUntil > 0) return directTypingUntil;

  const matchedEntry = Object.entries(typingByInterlocutor).find(([key]) => String(key || "").trim() === normalizedId);
  return Number(matchedEntry?.[1] || 0);
};

const toDateKey = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    const prefixedDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    return prefixedDate ? prefixedDate[1] : null;
  }
  if (typeof value === "object") {
    const dateLike = (value as any)?.date ?? (value as any)?.bookingDate ?? (value as any)?.day ?? null;
    return toDateKey(dateLike);
  }
  return null;
};

const getTodayDateKey = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isPastPendingReservationGroup = (group: ReservationGroupItem): boolean => {
  const dateKeys = group.dates
    .map((entry) => entry.date)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  if (dateKeys.length === 0) return false;
  if (group.dates.some((entry) => entry.booked === true)) return false;
  return dateKeys[dateKeys.length - 1] < getTodayDateKey();
};

const toBookedFlag = (value: unknown): boolean | null => {
  if (!value || typeof value !== "object") return null;
  if (!("booked" in (value as Record<string, unknown>))) return null;
  const raw = (value as { booked?: unknown }).booked;
  if (raw === null) return null;
  if (typeof raw === "boolean") return raw;
  return null;
};

const toBookingGroup = (entry: unknown): string | null => {
  if (!entry || typeof entry !== "object") return null;
  const raw = (entry as any)?.groupe ?? (entry as any)?.group;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed || null;
};

const normalizeAd = (payload: any): Ad => {
  const raw = payload?.data && typeof payload.data === "object" ? payload.data : payload;
  const isRenderablePhotoSrc = (value: unknown): value is string => {
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    return /^https?:\/\//i.test(trimmed) || trimmed.startsWith("/upload/");
  };
  const normalizedPhotos = Array.isArray(raw?.photos)
    ? raw.photos
        .map((photo: any) =>
          typeof photo === "string"
            ? photo
            : photo?.url || photo?.name || photo?.path || photo?.src
        )
        .filter(isRenderablePhotoSrc)
    : [];

  return {
    ...raw,
    id: raw?.id ?? raw?._id,
    title_i18n: raw?.title_i18n || { fr: "", en: "", ar: "" },
    content_i18n: raw?.content_i18n || { fr: "", en: "", ar: "" },
    adress_i18n: raw?.adress_i18n || { fr: raw?.adress || "", en: "", ar: "" },
    photos: normalizedPhotos,
  } as Ad;
};

const truncate = (text: string, max = 180): string => {
  if (!text) return "";
  const normalized = text.trim();
  if (normalized.length <= max) return normalized;
  return `${normalized.slice(0, max).trimEnd()}...`;
};

const capitalizeWords = (value: string): string =>
  value.replace(/\p{L}[\p{L}\p{M}'’-]*/gu, (word) => word.charAt(0).toLocaleUpperCase() + word.slice(1));

const toUniqueSortedDates = (entries: unknown[]): ReservationDateItem[] => {
  const byDate = new Map<string, ReservationDateItem>();
  entries.forEach((entry) => {
    const dateKey = toDateKey((entry as any)?.date ?? entry);
    if (!dateKey) return;
    const booked = toBookedFlag(entry);
    const reservationColor = normalizeReservationColor((entry as any)?.reservationColor);
    const prev = byDate.get(dateKey);
    if (!prev) {
      byDate.set(dateKey, { date: dateKey, booked, reservationColor });
      return;
    }
    const nextBooked =
      prev.booked === true || booked === true
        ? true
        : prev.booked === false || booked === false
          ? false
          : null;
    byDate.set(dateKey, { date: dateKey, booked: nextBooked, reservationColor: prev.reservationColor || reservationColor });
  });
  return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
};

const ReservationManagementPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || "fr";
  const { user: authUser, chatReadByInterlocutor, chatTypingByInterlocutor, latestChatMessage } = useSelector((state: RootState) => state.auth);
  const ownerId = String(authUser?.id || authUser?._id || "");

  const [items, setItems] = useState<OwnerReservationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmActionState | null>(null);
  const [openChatReservationId, setOpenChatReservationId] = useState<string | null>(null);
  const [expandedClientInfoReservationId, setExpandedClientInfoReservationId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<UiMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatDeleting, setChatDeleting] = useState(false);
  const [chatDeleteCandidate, setChatDeleteCandidate] = useState<UiMessage | null>(null);
  const [chatDraft, setChatDraft] = useState("");
  const [typingClock, setTypingClock] = useState(Date.now());
  const [editingManualClientReservation, setEditingManualClientReservation] = useState<OwnerReservationItem | null>(null);
  const [manualClientEditForm, setManualClientEditForm] = useState<ManualClientEditForm>(createEmptyManualClientEditForm);
  const [manualClientEditErrors, setManualClientEditErrors] = useState<ManualClientEditErrors>({});
  const [manualClientEditPhoneMaskDigits, setManualClientEditPhoneMaskDigits] = useState<number | null>(null);
  const [manualClientEditSaving, setManualClientEditSaving] = useState(false);
  const [openColorGroupId, setOpenColorGroupId] = useState<string | null>(null);
  const [colorSavingGroupId, setColorSavingGroupId] = useState<string | null>(null);
  const [clientSearchFilter, setClientSearchFilter] = useState("");
  const [dateFilterStart, setDateFilterStart] = useState("");
  const [dateFilterEnd, setDateFilterEnd] = useState("");
  const chatBodyRef = useRef<HTMLDivElement | null>(null);
  const inlineChatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const inlineTypingRef = useRef<HTMLDivElement | null>(null);
  const inlineChatBottomRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const typingStopTimerRef = useRef<number | null>(null);
  const typingActiveRef = useRef(false);
  const inlineSendingRef = useRef(false);
  const inlineClientMessageIdRef = useRef<string | null>(null);
  const pendingAutoScrollRef = useRef(false);

  const loadOwnerReservations = useCallback(async ({ cursor, append }: { cursor?: string | null; append: boolean } = { append: false, cursor: null }) => {
    if (!ownerId) {
      setItems([]);
      setHasMore(false);
      setNextCursor(null);
      return;
    }

    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError("");
    }
    try {
      const response = await getBookingsByOwnerAdsApi(ownerId, {
        limit: RESERVATIONS_PAGE_LIMIT,
        cursor: cursor || undefined,
      });
      const ownerBookings = Array.isArray(response?.data?.data) ? response.data.data : [];
      const groupedByAdAndUser = new Map<string, OwnerReservationItem>();
      const groupsByReservationKey = new Map<string, Map<string, ReservationGroupItem>>();

      ownerBookings.forEach((booking: any) => {
        const ad = normalizeAd(booking?.ads);
        const adId = String(ad?.id || (ad as any)?._id || "");
        if (!adId) return;

        const bookingUser = booking?.user;
        const reservationUserId = String(bookingUser?.id || bookingUser?._id || "");
        if (!reservationUserId || reservationUserId === ownerId) return;

        const reservationKey = `${adId}-${reservationUserId}`;
        if (!groupedByAdAndUser.has(reservationKey)) {
          groupedByAdAndUser.set(reservationKey, {
            id: reservationKey,
            ad,
            user: {
              id: reservationUserId,
              firstName: bookingUser?.firstName || "",
              lastName: bookingUser?.lastName || "",
              email: bookingUser?.email || "",
              phone: bookingUser?.phone || "",
              status: bookingUser?.status || "",
              nbrChild: Number.isInteger(Number(bookingUser?.nbr_child)) ? Number(bookingUser?.nbr_child) : null,
              country: bookingUser?.country || "",
              indicatif: bookingUser?.indicatif || "",
              codeCountry: bookingUser?.codeCountry || "",
            },
            groups: [],
          });
          groupsByReservationKey.set(reservationKey, new Map<string, ReservationGroupItem>());
        }

        const bookingId = String(booking?.id || booking?._id || "");
        const groupMap = groupsByReservationKey.get(reservationKey);
        if (!groupMap) return;

        const incomingEntries = Array.isArray(booking?.bookingDates) ? booking.bookingDates : [];
        incomingEntries.forEach((entry: any, entryIndex: number) => {
          const dateKey = toDateKey((entry as any)?.date ?? entry);
          if (!dateKey) return;
          const bookingGroup = toBookingGroup(entry) || `legacy-${bookingId || adId}-${reservationUserId}`;
          const groupKey = `${reservationKey}-${bookingGroup}`;
          const reservationColor = normalizeReservationColor((entry as any)?.reservationColor) || getStableReservationColor(bookingId || groupKey);
          const dateEntry: ReservationDateItem = { date: dateKey, booked: toBookedFlag(entry), reservationColor };

          if (!groupMap.has(groupKey)) {
            groupMap.set(groupKey, {
              id: groupKey,
              bookingId,
              bookingGroup,
              dates: [dateEntry],
              reservationColor,
              stayReview: booking?.stayReview || null,
            });
            return;
          }

          const existingGroup = groupMap.get(groupKey);
          if (!existingGroup) return;
          existingGroup.dates = toUniqueSortedDates([...existingGroup.dates, dateEntry]);
          existingGroup.reservationColor = existingGroup.reservationColor || reservationColor;
          existingGroup.stayReview = existingGroup.stayReview || booking?.stayReview || null;
        });
      });

      const nextItems = Array.from(groupedByAdAndUser.values())
        .map((reservation) => {
          const groupMap = groupsByReservationKey.get(reservation.id) ?? new Map<string, ReservationGroupItem>();
          const groups = Array.from(groupMap.values())
            .map((group) => ({
              ...group,
              dates: toUniqueSortedDates(group.dates),
            }))
            .filter((group) => group.dates.length > 0)
            .sort((a, b) => {
              const leftStart = a.dates[0]?.date || "";
              const rightStart = b.dates[0]?.date || "";
              return leftStart.localeCompare(rightStart);
            });
          return { ...reservation, groups };
        })
        .filter((reservation) => reservation.groups.length > 0);

      nextItems.sort((a, b) => {
        const left = `${a.user.lastName} ${a.user.firstName}`.trim().toLowerCase();
        const right = `${b.user.lastName} ${b.user.firstName}`.trim().toLowerCase();
        if (left !== right) return left.localeCompare(right);
        const leftAd = (getLocalizedText(a.ad.title_i18n, locale) || "").toLowerCase();
        const rightAd = (getLocalizedText(b.ad.title_i18n, locale) || "").toLowerCase();
        return leftAd.localeCompare(rightAd);
      });

      setItems((prev) => {
        if (!append) return nextItems;

        const mergedByReservation = new Map<string, OwnerReservationItem>();
        [...prev, ...nextItems].forEach((reservation) => {
          const existing = mergedByReservation.get(reservation.id);
          if (!existing) {
            mergedByReservation.set(reservation.id, reservation);
            return;
          }

          const groupById = new Map<string, ReservationGroupItem>();
          [...existing.groups, ...reservation.groups].forEach((group) => {
            const currentGroup = groupById.get(group.id);
            if (!currentGroup) {
              groupById.set(group.id, group);
              return;
            }
            groupById.set(group.id, {
              ...currentGroup,
              reservationColor: currentGroup.reservationColor || group.reservationColor,
              dates: toUniqueSortedDates([...currentGroup.dates, ...group.dates]),
              stayReview: currentGroup.stayReview || group.stayReview || null,
            });
          });

          mergedByReservation.set(reservation.id, {
            ...existing,
            groups: Array.from(groupById.values()).sort((a, b) => {
              const leftStart = a.dates[0]?.date || "";
              const rightStart = b.dates[0]?.date || "";
              return leftStart.localeCompare(rightStart);
            }),
          });
        });

        return Array.from(mergedByReservation.values());
      });
      setHasMore(Boolean(response?.data?.hasMore));
      setNextCursor(response?.data?.nextCursor ?? null);
    } catch (err: any) {
      if (!append) {
        setItems([]);
        setError(err?.response?.data?.message || t("myReservations.loadError"));
      }
    } finally {
      if (append) setLoadingMore(false);
      else setLoading(false);
    }
  }, [locale, ownerId, t]);

  useEffect(() => {
    void loadOwnerReservations({ append: false, cursor: null });
  }, [loadOwnerReservations]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    if (!hasMore || !nextCursor || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && nextCursor && !loadingMore) {
          void loadOwnerReservations({ append: true, cursor: nextCursor });
        }
      },
      { rootMargin: "240px 0px" }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loadOwnerReservations, loading, loadingMore, nextCursor]);

  const getReservationById = useCallback((reservationId: string) => {
    return items.find((entry) => entry.id === reservationId) || null;
  }, [items]);

  const getGroupById = useCallback((reservation: OwnerReservationItem | null, groupId: string) => {
    if (!reservation) return null;
    return reservation.groups.find((entry) => entry.id === groupId) || null;
  }, []);

  const openChatReservation = useMemo(() => {
    if (!openChatReservationId) return null;
    return items.find((entry) => entry.id === openChatReservationId) || null;
  }, [items, openChatReservationId]);

  const activeInterlocutorId = isRobotizedClientEmail(openChatReservation?.user.email)
    ? ""
    : String(openChatReservation?.user.id || "");

  useEffect(() => {
    if (!openChatReservationId) return;
    const reservation = items.find((entry) => entry.id === openChatReservationId);
    if (isRobotizedClientEmail(reservation?.user.email)) {
      setOpenChatReservationId(null);
      setChatMessages([]);
      setChatDraft("");
    }
  }, [items, openChatReservationId]);

  const getConfirmedDatesForOtherUsers = (reservation: OwnerReservationItem) => {
    const reservationAdId = String(reservation.ad.id || (reservation.ad as any)?._id || "");
    const reservationUserId = String(reservation.user.id || "");
    const confirmedDates = new Set<string>();

    items.forEach((item) => {
      const itemAdId = String(item.ad.id || (item.ad as any)?._id || "");
      const itemUserId = String(item.user.id || "");
      if (!itemAdId || !itemUserId) return;
      if (itemAdId !== reservationAdId || itemUserId === reservationUserId) return;
      item.groups.forEach((group) => {
        group.dates.forEach((dateEntry) => {
          if (dateEntry.booked === true && dateEntry.date) confirmedDates.add(dateEntry.date);
        });
      });
    });

    return confirmedDates;
  };

  const filteredItems = useMemo(() => {
    const normalizedSearch = normalizeFilterText(clientSearchFilter);
    const normalizedPhoneSearch = normalizeFilterPhone(clientSearchFilter);
    const hasSearchFilter = normalizedSearch.length > 0 || normalizedPhoneSearch.length > 0;
    const hasDateFilter = Boolean(dateFilterStart || dateFilterEnd);
    const rangeStart = dateFilterStart && dateFilterEnd
      ? [dateFilterStart, dateFilterEnd].sort()[0]
      : (dateFilterStart || dateFilterEnd);
    const rangeEnd = dateFilterStart && dateFilterEnd
      ? [dateFilterStart, dateFilterEnd].sort()[1]
      : (dateFilterStart || dateFilterEnd);

    if (!hasSearchFilter && !hasDateFilter) return items;

    return items
      .map((item) => {
        const searchableText = normalizeFilterText([
          item.user.firstName,
          item.user.lastName,
          `${item.user.firstName} ${item.user.lastName}`,
          `${item.user.lastName} ${item.user.firstName}`,
          item.user.email,
          item.user.phone,
          item.user.indicatif,
          `${item.user.indicatif} ${item.user.phone}`,
        ].filter(Boolean).join(" "));
        const searchablePhone = normalizeFilterPhone(`${item.user.indicatif}${item.user.phone}`);
        const matchesSearch =
          !hasSearchFilter ||
          (normalizedSearch && searchableText.includes(normalizedSearch)) ||
          (normalizedPhoneSearch && searchablePhone.includes(normalizedPhoneSearch));

        if (!matchesSearch) return null;

        const groups = hasDateFilter
          ? item.groups.filter((group) => group.dates.some((entry) => (
            entry.date >= rangeStart && entry.date <= rangeEnd
          )))
          : item.groups;

        if (groups.length === 0) return null;
        return { ...item, groups };
      })
      .filter((item): item is OwnerReservationItem => Boolean(item));
  }, [clientSearchFilter, dateFilterEnd, dateFilterStart, items]);

  const hasActiveFilters = Boolean(clientSearchFilter.trim() || dateFilterStart || dateFilterEnd);

  const clearReservationFilters = () => {
    setClientSearchFilter("");
    setDateFilterStart("");
    setDateFilterEnd("");
  };

  const formatClientStatus = useCallback((value?: string): string => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return t("myReservations.unknownField");
    if (normalized === "single" || normalized === "celibataire" || normalized === "célibataire") {
      return t("signup.statusSingle");
    }
    if (normalized === "married" || normalized === "marie_sans_enfants" || normalized === "marié") {
      return t("signup.statusMarriedNoChildren");
    }
    if (
      normalized === "married_with_children" ||
      normalized === "marie_avec_enfants" ||
      normalized === "marié avec enfants"
    ) {
      return t("signup.statusMarriedWithChildren");
    }
    return value?.trim() || t("myReservations.unknownField");
  }, [t]);

  const resolveApiErrorMessage = useCallback((err: any, fallback: string): string => {
    const apiMessage = err?.response?.data?.message ?? err?.response?.data?.msg;
    if (typeof apiMessage === "string" && apiMessage.trim()) return apiMessage.trim();
    if (apiMessage && typeof apiMessage === "object") {
      const currentLang = (i18n.resolvedLanguage || "fr").split("-")[0];
      const byLang = (apiMessage as Record<string, unknown>)[currentLang];
      if (typeof byLang === "string" && byLang.trim()) return byLang.trim();
      const fallbackMessage =
        (apiMessage as Record<string, unknown>).fr ||
        (apiMessage as Record<string, unknown>).en ||
        (apiMessage as Record<string, unknown>).ar;
      if (typeof fallbackMessage === "string" && fallbackMessage.trim()) return fallbackMessage.trim();
    }
    if (typeof err?.message === "string" && err.message.trim()) return err.message.trim();
    return fallback;
  }, [i18n.resolvedLanguage]);

  const openManualClientEditPopup = useCallback((reservation: OwnerReservationItem) => {
    const normalizedStatus = normalizeUserStatus(reservation.user.status);
    setEditingManualClientReservation(reservation);
    setManualClientEditForm({
      firstName: reservation.user.firstName || "",
      lastName: reservation.user.lastName || "",
      phone: reservation.user.phone || "",
      status: normalizedStatus,
      nbr_child: normalizedStatus === "married_with_children" ? reservation.user.nbrChild ?? "" : "",
      country: getCountryValueDisplay(reservation.user.country, "", locale),
      indicatif: reservation.user.indicatif || "",
      codeCountry: reservation.user.codeCountry || "",
    });
    setManualClientEditErrors({});
    setManualClientEditPhoneMaskDigits(null);
  }, [locale]);

  const closeManualClientEditPopup = useCallback(() => {
    if (manualClientEditSaving) return;
    setEditingManualClientReservation(null);
    setManualClientEditForm(createEmptyManualClientEditForm());
    setManualClientEditErrors({});
    setManualClientEditPhoneMaskDigits(null);
  }, [manualClientEditSaving]);

  const handleManualClientEditInputChange = useCallback((event: any) => {
    const target = event.target;
    const name = target.name as keyof ManualClientEditForm;
    const value = target.value;
    const mask = target.mask || "";
    const indicatif = target.indicatif || "";

    setManualClientEditForm((prev) => {
      if (name === "status") {
        const normalizedStatus = normalizeUserStatus(value);
        return {
          ...prev,
          status: normalizedStatus,
          nbr_child: normalizedStatus === "married_with_children" ? prev.nbr_child : "",
          indicatif: indicatif || prev.indicatif,
          country: target.country || prev.country,
          codeCountry: target.code || prev.codeCountry,
        };
      }

      if (name === "nbr_child") {
        return {
          ...prev,
          nbr_child: value === "" ? "" : Number(value),
          indicatif: indicatif || prev.indicatif,
          country: target.country || prev.country,
          codeCountry: target.code || prev.codeCountry,
        };
      }

      return {
        ...prev,
        indicatif: indicatif || prev.indicatif,
        country: target.country || prev.country,
        codeCountry: target.code || prev.codeCountry,
        [name]: value,
      };
    });

    setManualClientEditErrors((prev) => ({ ...prev, [name]: "" }));

    if (name === "phone" && mask) {
      setManualClientEditPhoneMaskDigits((mask.match(/\d/g) || []).length);
    }
  }, []);

  const validateManualClientEditForm = useCallback((): ManualClientEditErrors => {
    const formErrors: ManualClientEditErrors = {};
    const status = normalizeUserStatus(manualClientEditForm.status);

    if (!manualClientEditForm.lastName.trim()) formErrors.lastName = t("adDetails.errors.lastNameRequired");
    if (!manualClientEditForm.firstName.trim()) formErrors.firstName = t("adDetails.errors.firstNameRequired");
    if (!status) formErrors.status = t("adDetails.errors.statusRequired");

    if (status === "married_with_children") {
      if (
        manualClientEditForm.nbr_child === "" ||
        !Number.isInteger(Number(manualClientEditForm.nbr_child)) ||
        Number(manualClientEditForm.nbr_child) < 0 ||
        Number(manualClientEditForm.nbr_child) > 7
      ) {
        formErrors.nbr_child = t("adDetails.errors.nbrChildRequired");
      }
    }

    if (hasLeadingZeroPhoneDigit(manualClientEditForm.phone)) {
      formErrors.phone = t("adDetails.errors.phoneLeadingZero");
    } else if (manualClientEditForm.phone.trim() && manualClientEditPhoneMaskDigits && manualClientEditPhoneMaskDigits > 0) {
      const enteredDigits = manualClientEditForm.phone.replace(/\D/g, "").length;
      if (enteredDigits !== manualClientEditPhoneMaskDigits) {
        formErrors.phone = t("adDetails.errors.phoneExact", { count: manualClientEditPhoneMaskDigits });
      }
    }

    return formErrors;
  }, [manualClientEditForm, manualClientEditPhoneMaskDigits, t]);

  const handleManualClientEditSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingManualClientReservation) return;

    const bookingId = editingManualClientReservation.groups.find((group) => group.bookingId)?.bookingId || "";
    if (!bookingId) {
      toast.error(t("myReservations.editManualClientError"));
      return;
    }

    const formErrors = validateManualClientEditForm();
    setManualClientEditErrors(formErrors);
    if (Object.keys(formErrors).length > 0) {
      toast.error(t("adDetails.invalidUserForm"));
      return;
    }

    const normalizedStatus = normalizeUserStatus(manualClientEditForm.status);
    if (!normalizedStatus) {
      toast.error(t("adDetails.invalidUserForm"));
      return;
    }
    const payload = {
      firstName: manualClientEditForm.firstName.trim(),
      lastName: manualClientEditForm.lastName.trim(),
      phone: manualClientEditForm.phone.trim(),
      status: normalizedStatus,
      nbr_child: normalizedStatus === "married_with_children" ? Number(manualClientEditForm.nbr_child) : 0,
      country: manualClientEditForm.country,
      indicatif: manualClientEditForm.indicatif,
      codeCountry: manualClientEditForm.codeCountry,
    };

    setManualClientEditSaving(true);
    try {
      const response = await updateManualBookingClientApi(bookingId, payload);
      const updatedUser = response?.data?.data?.user || payload;

      setItems((prev) => prev.map((item) => {
        if (item.id !== editingManualClientReservation.id) return item;
        return {
          ...item,
          user: {
            ...item.user,
            firstName: updatedUser.firstName ?? payload.firstName,
            lastName: updatedUser.lastName ?? payload.lastName,
            phone: updatedUser.phone ?? payload.phone,
            status: updatedUser.status ?? payload.status,
            nbrChild: Number.isInteger(Number(updatedUser.nbr_child))
              ? Number(updatedUser.nbr_child)
              : payload.nbr_child,
            country: updatedUser.country ?? payload.country,
            indicatif: updatedUser.indicatif ?? payload.indicatif,
            codeCountry: updatedUser.codeCountry ?? payload.codeCountry,
          },
        };
      }));

      toast.success(t("myReservations.editManualClientSuccess"));
      setEditingManualClientReservation(null);
      setManualClientEditForm(createEmptyManualClientEditForm());
      setManualClientEditErrors({});
      setManualClientEditPhoneMaskDigits(null);
    } catch (err: any) {
      toast.error(resolveApiErrorMessage(err, t("myReservations.editManualClientError")));
    } finally {
      setManualClientEditSaving(false);
    }
  }, [
    editingManualClientReservation,
    manualClientEditForm,
    resolveApiErrorMessage,
    t,
    validateManualClientEditForm,
  ]);

  const requestGroupAction = (
    reservation: OwnerReservationItem,
    group: ReservationGroupItem,
    mode?: ConfirmActionState["mode"],
  ) => {
    const hasConfirmedDate = group.dates.some((entry) => entry.booked === true);
    setConfirmAction({
      mode: mode || (hasConfirmedDate ? "cancel" : "validate"),
      reservationId: reservation.id,
      groupId: group.id,
    });
  };

  const confirmActionSummary = useMemo(() => {
    if (!confirmAction) return "";
    const reservation = getReservationById(confirmAction.reservationId);
    const group = getGroupById(reservation, confirmAction.groupId);
    if (!group || group.dates.length === 0) return t("myReservations.unknownDate");
    const first = group.dates[0]?.date || "";
    const last = group.dates[group.dates.length - 1]?.date || "";
    return first === last ? first : `${first} → ${last}`;
  }, [confirmAction, getGroupById, getReservationById, t]);

  const executeReservationAction = async () => {
    if (!confirmAction) return;
    const reservation = getReservationById(confirmAction.reservationId);
    const group = getGroupById(reservation, confirmAction.groupId);
    if (!reservation || !group) return;

    const adId = String(reservation.ad.id || (reservation.ad as any)?._id || "");
    if (!adId || !reservation.user.id || group.dates.length === 0) return;

    setActionLoading(true);
    setError("");
    try {
      if (confirmAction.mode === "deletePending") {
        await cancelBookingByDatesApi({
          id_user: reservation.user.id,
          id_ads: adId,
          groupe: group.bookingGroup,
          bookingDates: group.dates.map((entry) => entry.date),
          cancelledByOwner: true,
        });

        setItems((prev) => prev
          .map((item) => {
            if (item.id !== reservation.id) return item;
            return {
              ...item,
              groups: item.groups.filter((entry) => entry.id !== group.id),
            };
          })
          .filter((item) => item.groups.length > 0));
        setConfirmAction(null);
        return;
      }

      const nextBookedState = confirmAction.mode === "validate";
      await validateBookingByDateApi({
        id_user: reservation.user.id,
        id_ads: adId,
        date: group.dates[0].date,
        booked: nextBookedState,
        groupe: group.bookingGroup,
        bookingDates: group.dates.map((entry) => entry.date),
      });

      setItems((prev) => prev.map((item) => {
        if (item.id !== reservation.id) return item;
        return {
          ...item,
          groups: item.groups.map((entry) => {
            if (entry.id !== group.id) return entry;
            return {
              ...entry,
              dates: entry.dates.map((dateEntry) => ({
                ...dateEntry,
                booked: nextBookedState,
              })),
            };
          }),
        };
      }));
      setConfirmAction(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || t("myReservations.actionError"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReservationColorChange = async (
    reservation: OwnerReservationItem,
    group: ReservationGroupItem,
    reservationColor: string,
  ) => {
    if (!group.bookingId || colorSavingGroupId) return;
    setColorSavingGroupId(group.id);
    try {
      await updateBookingReservationColorApi(group.bookingId, {
        groupe: group.bookingGroup,
        reservationColor,
      });

      setItems((prev) => prev.map((item) => {
        if (item.id !== reservation.id) return item;
        return {
          ...item,
          groups: item.groups.map((entry) => {
            if (entry.id !== group.id) return entry;
            return {
              ...entry,
              reservationColor,
              dates: entry.dates.map((dateEntry) => ({
                ...dateEntry,
                reservationColor,
              })),
            };
          }),
        };
      }));
      setOpenColorGroupId(null);
      toast.success(t("myReservations.colorUpdateSuccess"));
    } catch (err: any) {
      toast.error(resolveApiErrorMessage(err, t("myReservations.colorUpdateError")));
    } finally {
      setColorSavingGroupId(null);
    }
  };

  const scrollInlineChatToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    if (!chatBodyRef.current) return;
    chatBodyRef.current.scrollTo({ top: chatBodyRef.current.scrollHeight, behavior });
  }, []);

  const forceScrollInlineChatToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    scrollInlineChatToBottom(behavior);
    window.requestAnimationFrame(() => scrollInlineChatToBottom(behavior));
    window.setTimeout(() => scrollInlineChatToBottom("auto"), 60);
    window.setTimeout(() => scrollInlineChatToBottom("auto"), 160);
    window.setTimeout(() => scrollInlineChatToBottom("auto"), 320);
  }, [scrollInlineChatToBottom]);

  useEffect(() => {
    const timer = window.setInterval(() => setTypingClock(Date.now()), 600);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    return () => {
      if (typingStopTimerRef.current) {
        window.clearTimeout(typingStopTimerRef.current);
        typingStopTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!activeInterlocutorId) {
      setChatMessages([]);
      setChatDraft("");
      return;
    }

    let cancelled = false;
    setChatLoading(true);

    const loadMessages = async () => {
      try {
        const response = await listCurrentUserMessagesByInterlocutorApi(activeInterlocutorId, 120);
        const incoming = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (cancelled) return;
        setChatMessages(
          incoming.map((item: any) => ({
            id: String(item?.id || item?._id || `${item?.createdAt || Date.now()}`),
            content: typeof item?.content === "string" ? item.content : "",
            isMine: Boolean(item?.isMine),
            createdAt: typeof item?.createdAt === "string" ? item.createdAt : new Date().toISOString(),
            readAt: typeof item?.readAt === "string" ? item.readAt : undefined,
          }))
        );
        await markCurrentUserConversationAsReadApi(activeInterlocutorId);
      } catch (_error) {
        if (cancelled) return;
        setChatMessages([]);
      } finally {
        if (!cancelled) setChatLoading(false);
      }
    };

    void loadMessages();
    return () => {
      cancelled = true;
    };
  }, [activeInterlocutorId]);

  useEffect(() => {
    if (!activeInterlocutorId || !latestChatMessage?.id) return;
    if (latestChatMessage.interlocutorId !== activeInterlocutorId) return;

    setChatMessages((prev) => {
      return upsertChatMessageById(prev, {
        id: latestChatMessage.id,
        content: latestChatMessage.content,
        isMine: latestChatMessage.isMine,
        createdAt: latestChatMessage.createdAt,
        readAt: latestChatMessage.readAt,
      });
    });

    pendingAutoScrollRef.current = true;

    if (!latestChatMessage.isMine) {
      void markCurrentUserConversationAsReadApi(activeInterlocutorId);
    }
  }, [activeInterlocutorId, latestChatMessage]);

  const activeInterlocutorTypingUntil = getTypingUntilForInterlocutor(chatTypingByInterlocutor, activeInterlocutorId);
  const isActiveInterlocutorTyping = Boolean(activeInterlocutorTypingUntil > typingClock);

  useLayoutEffect(() => {
    if (!openChatReservationId) return;
    if (!chatMessages.length && !isActiveInterlocutorTyping) return;
    forceScrollInlineChatToBottom(pendingAutoScrollRef.current ? "smooth" : "auto");
    pendingAutoScrollRef.current = false;
  }, [chatMessages, forceScrollInlineChatToBottom, isActiveInterlocutorTyping, openChatReservationId]);

  useEffect(() => {
    if (!openChatReservationId || !isActiveInterlocutorTyping) return;
    pendingAutoScrollRef.current = true;
    const scrollToTyping = (behavior: ScrollBehavior = "auto") => {
      inlineChatBottomRef.current?.scrollIntoView({ behavior, block: "end" });
      forceScrollInlineChatToBottom(behavior);
    };

    window.requestAnimationFrame(() => scrollToTyping("smooth"));
    window.setTimeout(scrollToTyping, 80);
    window.setTimeout(scrollToTyping, 220);
  }, [activeInterlocutorTypingUntil, forceScrollInlineChatToBottom, isActiveInterlocutorTyping, openChatReservationId]);

  useEffect(() => {
    if (!openChatReservationId) return;
    pendingAutoScrollRef.current = true;
    forceScrollInlineChatToBottom("auto");
  }, [forceScrollInlineChatToBottom, openChatReservationId]);

  useEffect(() => {
    if (!activeInterlocutorId) return;
    const readAt = chatReadByInterlocutor[activeInterlocutorId];
    if (!readAt) return;
    setChatMessages((prev) => prev.map((message) => (
      message.isMine && !message.readAt ? { ...message, readAt } : message
    )));
  }, [activeInterlocutorId, chatReadByInterlocutor]);

  const lastChatMessage = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null;
  const showSeen = Boolean(lastChatMessage?.isMine && (lastChatMessage?.readAt || (activeInterlocutorId ? chatReadByInterlocutor[activeInterlocutorId] : undefined)));

  const handleToggleClientChat = useCallback((reservationId: string) => {
    const reservation = items.find((entry) => entry.id === reservationId);
    if (isRobotizedClientEmail(reservation?.user.email)) return;
    setOpenChatReservationId((prev) => prev === reservationId ? null : reservationId);
    setChatDraft("");
    setChatDeleteCandidate(null);
    if (inlineChatInputRef.current) {
      inlineChatInputRef.current.style.height = `${INLINE_CHAT_INPUT_MIN_HEIGHT}px`;
    }
    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }
    if (typingActiveRef.current && activeInterlocutorId) {
      typingActiveRef.current = false;
      sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: false }).catch(() => undefined);
    }
  }, [activeInterlocutorId, items]);

  const handleInlineChatDraftChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    resizeInlineChatInputElement(event.target);
    setChatDraft(nextValue);
    if (!activeInterlocutorId) return;

    const hasText = nextValue.trim().length > 0;
    if (hasText && !typingActiveRef.current) {
      typingActiveRef.current = true;
      sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: true }).catch(() => undefined);
    }

    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }

    if (!hasText) {
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: false }).catch(() => undefined);
      }
      return;
    }

    typingStopTimerRef.current = window.setTimeout(() => {
      if (!typingActiveRef.current || !activeInterlocutorId) return;
      typingActiveRef.current = false;
      sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: false }).catch(() => undefined);
    }, 1400);
  };

  const handleInlineChatSend = useCallback(async () => {
    if (!activeInterlocutorId) return;
    if (inlineSendingRef.current) return;
    const content = chatDraft.trim();
    if (!content) return;

    inlineSendingRef.current = true;
    const clientMessageId = inlineClientMessageIdRef.current || createClientMessageId();
    inlineClientMessageIdRef.current = clientMessageId;
    pendingAutoScrollRef.current = true;
    setChatSending(true);
    try {
      await sendCurrentUserMessageApi({
        toUserId: activeInterlocutorId,
        content,
        clientMessageId,
      });
      setChatDraft("");
      if (inlineChatInputRef.current) {
        inlineChatInputRef.current.style.height = `${INLINE_CHAT_INPUT_MIN_HEIGHT}px`;
      }
      if (typingActiveRef.current) {
        typingActiveRef.current = false;
        sendCurrentUserTypingSignalApi({ toUserId: activeInterlocutorId, isTyping: false }).catch(() => undefined);
      }
      forceScrollInlineChatToBottom("smooth");
    } finally {
      inlineSendingRef.current = false;
      inlineClientMessageIdRef.current = null;
      setChatSending(false);
    }
  }, [activeInterlocutorId, chatDraft, forceScrollInlineChatToBottom]);

  const handleConfirmDeleteInlineChatMessage = useCallback(async () => {
    if (!chatDeleteCandidate?.id) return;
    setChatDeleting(true);
    try {
      await deleteCurrentUserMessageApi(chatDeleteCandidate.id);
      setChatMessages((prev) => prev.filter((message) => message.id !== chatDeleteCandidate.id));
      setChatDeleteCandidate(null);
      pendingAutoScrollRef.current = true;
      forceScrollInlineChatToBottom("auto");
    } finally {
      setChatDeleting(false);
    }
  }, [chatDeleteCandidate, forceScrollInlineChatToBottom]);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        <header className={styles.header}>
          <h1>{t("myReservations.title")}</h1>
          <p>{t("myReservations.subtitleOwner")}</p>
        </header>

        {loading && <p className={styles.state}>{t("myReservations.loading")}</p>}
        {!loading && error && <p className={styles.state}>{t("myReservations.error", { error })}</p>}
        {!loading && !error && items.length === 0 && <p className={styles.state}>{t("myReservations.emptyOwner")}</p>}

        {!loading && !error && items.length > 0 && (
          <div className={styles.ownerReservationFilters} role="search" aria-label={t("myReservations.filtersTitle")}>
            <label className={styles.ownerReservationFilterField}>
              <span>{t("myReservations.clientSearchLabel")}</span>
              <input
                type="search"
                value={clientSearchFilter}
                onChange={(event) => setClientSearchFilter(event.target.value)}
                placeholder={t("myReservations.clientSearchPlaceholder")}
              />
            </label>
            <div className={styles.ownerReservationFilterField}>
              <div className={styles.ownerReservationDateFilterRow}>
                <label>
                  <span>{t("myReservations.dateStartLabel")}</span>
                  <input
                    type="date"
                    value={dateFilterStart}
                    onChange={(event) => setDateFilterStart(event.target.value)}
                  />
                </label>
                <label>
                  <span>{t("myReservations.dateEndLabel")}</span>
                  <input
                    type="date"
                    value={dateFilterEnd}
                    onChange={(event) => setDateFilterEnd(event.target.value)}
                  />
                </label>
              </div>
            </div>
            <button
              type="button"
              className={styles.ownerReservationFilterClear}
              onClick={clearReservationFilters}
              disabled={!hasActiveFilters}
            >
              {t("myReservations.clearFilters")}
            </button>
          </div>
        )}

        {!loading && !error && items.length > 0 && filteredItems.length === 0 && (
          <p className={styles.state}>{t("myReservations.noFilterResults")}</p>
        )}

        {!loading && !error && filteredItems.length > 0 && (
          <section className={styles.list}>
            {filteredItems.map((item) => {
              const adId = item.ad.id || (item.ad as any)?._id;
              if (!adId) return null;

              const title = capitalizeWords(getLocalizedText(item.ad.title_i18n, locale) || t("myReservations.untitled"));
              const description = capitalizeWords(truncate(getLocalizedText(item.ad.content_i18n, locale)));
              const image = Array.isArray(item.ad.photos) && item.ad.photos[0] ? item.ad.photos[0] : placeholder;
              const fullName = capitalizeWords(`${item.user.lastName} ${item.user.firstName}`.trim() || t("myReservations.unknownUser"));
              const email = item.user.email?.trim() || "";
              const hasRobotizedEmail = isRobotizedClientEmail(email);
              const phone = [item.user.indicatif, item.user.phone].filter(Boolean).join(" ").trim() || t("myReservations.unknownField");
              const country = getCountryValueDisplay(item.user.country, t("myReservations.unknownField"), locale);
              const status = formatClientStatus(item.user.status);
              const children = item.user.nbrChild !== null ? String(item.user.nbrChild) : t("myReservations.unknownField");
              const countryCode = item.user.codeCountry?.trim() || t("myReservations.unknownField");
              const confirmedDatesForOtherUsers = getConfirmedDatesForOtherUsers(item);

              return (
                <article
                  key={item.id}
                  className={`${styles.item} ${styles.ownerReservationItemBtn}`}
                >
                  <Link to={`/ads/${adId}`} className={styles.ownerReservationMediaLink}>
                    <ShimmerImage className={styles.photo} src={image} alt={title} />
                  </Link>

                  <div className={styles.content}>
                    <h3>
                      <Link to={`/ads/${adId}`} className={styles.ownerReservationTitleLink}>
                        {title}
                      </Link>
                    </h3>
                    <p>
                      <Link to={`/ads/${adId}`} className={styles.ownerReservationDescriptionLink}>
                        {description || t("myReservations.noDescription")}
                      </Link>
                    </p>
                    <div className={styles.ownerReservationMetaRow}>
                      <p className={styles.ownerReservationMeta}>
                        {t("myReservations.bookedBy", { name: fullName })}
                      </p>
                      {!hasRobotizedEmail && (
                        <button
                          type="button"
                          className={styles.ownerReservationChatButton}
                          onClick={() => handleToggleClientChat(item.id)}
                          aria-label={`${t("header.messagesTitle")} ${fullName}`}
                          title="Contacter le client"
                          aria-expanded={openChatReservationId === item.id}
                        >
                          <MailIcon aria-hidden={true} focusable="false" />
                        </button>
                      )}
                      {hasRobotizedEmail && (
                        <button
                          type="button"
                          className={styles.ownerReservationEditButton}
                          onClick={() => openManualClientEditPopup(item)}
                          aria-label={t("myReservations.editManualClient")}
                          title={t("myReservations.editManualClient")}
                        >
                          <EditIcon aria-hidden={true} focusable="false" />
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.ownerReservationInfoToggle}
                      onClick={() => setExpandedClientInfoReservationId((prev) => prev === item.id ? null : item.id)}
                      aria-expanded={expandedClientInfoReservationId === item.id}
                    >
                      {expandedClientInfoReservationId === item.id
                        ? t("myReservations.hideClientInfo")
                        : t("myReservations.showClientInfo")}
                    </button>
                    <div
                      className={`${styles.ownerReservationUserDetailsPanel} ${expandedClientInfoReservationId === item.id ? styles.ownerReservationUserDetailsPanelOpen : ""}`}
                      aria-hidden={expandedClientInfoReservationId !== item.id}
                    >
                      <div className={styles.ownerReservationUserDetails}>
                        {!hasRobotizedEmail && (
                          <span>{t("myReservations.userEmail")} : {email || t("myReservations.unknownField")}</span>
                        )}
                        <span>{t("myReservations.userPhone")} : {phone}</span>
                        <span>{t("myReservations.userCountry")} : {country}</span>
                        <span>{t("myReservations.userStatus")} : {status}</span>
                        <span>{t("myReservations.userChildren")} : {children}</span>
                        <span>{t("myReservations.userCountryCode")} : {countryCode}</span>
                      </div>
                    </div>
                  </div>

                  <div
                    className={`${styles.ownerReservationInlineChatRow} ${openChatReservationId === item.id ? styles.ownerReservationInlineChatRowOpen : ""}`}
                    role="dialog"
                    aria-modal="false"
                    aria-label={t("header.chatWindowTitle", { name: fullName })}
                    aria-hidden={openChatReservationId !== item.id}
                  >
                    <div className={styles.ownerReservationInlineChat}>
                      <div className={styles.ownerReservationInlineChatHead}>
                        <div className={styles.ownerReservationInlineChatTitle}>{fullName}</div>
                      </div>
                      <div ref={openChatReservationId === item.id ? chatBodyRef : null} className={styles.ownerReservationInlineChatBody}>
                        {openChatReservationId !== item.id ? null : chatLoading ? (
                          <div className={styles.ownerReservationInlineChatEmpty}>{t("header.chatLoading")}</div>
                        ) : chatMessages.length === 0 && !isActiveInterlocutorTyping ? (
                          null
                        ) : (
                          chatMessages.map((message) => (
                            <div
                              key={message.id}
                              className={`${styles.ownerReservationInlineChatBubbleWrap} ${message.isMine ? styles.ownerReservationInlineChatBubbleWrapMine : styles.ownerReservationInlineChatBubbleWrapTheirs}`}
                            >
                              {message.isMine && (
                                <button
                                  type="button"
                                  className={styles.ownerReservationInlineChatBubbleMore}
                                  onClick={() => setChatDeleteCandidate(message)}
                                  aria-label={t("header.chatDeleteMenuAria")}
                                >
                                  <span aria-hidden="true">⋮</span>
                                </button>
                              )}
                              <div className={`${styles.ownerReservationInlineChatBubble} ${message.isMine ? styles.ownerReservationInlineChatBubbleMine : styles.ownerReservationInlineChatBubbleTheirs}`}>
                                <ChatMessageContent content={message.content} />
                              </div>
                            </div>
                          ))
                        )}
                        {openChatReservationId === item.id && isActiveInterlocutorTyping && (
                          <div ref={inlineTypingRef} className={styles.ownerReservationInlineChatTypingWrap}>
                            <div className={styles.ownerReservationInlineChatTyping} aria-live="polite" aria-label={t("header.chatTyping")}>
                              <span className={styles.ownerReservationInlineChatTypingText}>{t("header.chatTyping")}</span>
                              <span className={styles.ownerReservationInlineChatTypingDot} />
                              <span className={styles.ownerReservationInlineChatTypingDot} />
                              <span className={styles.ownerReservationInlineChatTypingDot} />
                            </div>
                          </div>
                        )}
                        {openChatReservationId === item.id && <div ref={inlineChatBottomRef} className={styles.ownerReservationInlineChatBottom} />}
                      </div>
                      {openChatReservationId === item.id && showSeen && (
                        <div className={styles.ownerReservationInlineChatSeen}>{t("header.chatSeen")}</div>
                      )}
                      <div className={styles.ownerReservationInlineChatFoot}>
                        <textarea
                          ref={openChatReservationId === item.id ? inlineChatInputRef : null}
                          value={openChatReservationId === item.id ? chatDraft : ""}
                          onChange={handleInlineChatDraftChange}
                          className={styles.ownerReservationInlineChatInput}
                          rows={1}
                          placeholder={t("header.chatPlaceholder")}
                          disabled={openChatReservationId !== item.id}
                        />
                        <button
                          type="button"
                          className={styles.ownerReservationInlineChatSend}
                          onClick={handleInlineChatSend}
                          disabled={openChatReservationId !== item.id || chatSending || !chatDraft.trim()}
                        >
                          {t("header.chatSend")}
                        </button>
                      </div>
                      {chatDeleteCandidate && (
                        <div className={styles.ownerReservationInlineChatDeleteModalOverlay} onClick={() => setChatDeleteCandidate(null)}>
                          <div className={styles.ownerReservationInlineChatDeleteModal} onClick={(event) => event.stopPropagation()}>
                            <p className={styles.ownerReservationInlineChatDeleteModalTitle}>{t("header.chatDeleteConfirmTitle")}</p>
                            <p className={styles.ownerReservationInlineChatDeleteModalText}>{t("header.chatDeleteConfirmText")}</p>
                            <div className={styles.ownerReservationInlineChatDeleteModalActions}>
                              <button
                                type="button"
                                className={styles.ownerReservationInlineChatDeleteCancel}
                                onClick={() => setChatDeleteCandidate(null)}
                                disabled={chatDeleting}
                              >
                                {t("header.chatDeleteCancel")}
                              </button>
                              <button
                                type="button"
                                className={styles.ownerReservationInlineChatDeleteConfirm}
                                onClick={handleConfirmDeleteInlineChatMessage}
                                disabled={chatDeleting}
                              >
                                {chatDeleting ? t("header.chatDeleteLoading") : t("header.chatDeleteConfirm")}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.ownerReservationExpandedBlock}>
                    <div className={styles.ownerReservationGroupsWrap}>
                      {item.groups.map((group) => {
                        const hasConfirmedDate = group.dates.some((entry) => entry.booked === true);
                        const isPastPendingGroup = isPastPendingReservationGroup(group);
                        const lockedByAnotherReservation =
                          !hasConfirmedDate &&
                          group.dates.some((entry) => confirmedDatesForOtherUsers.has(entry.date));
                        const actionClass = lockedByAnotherReservation
                          ? styles.userDateStatusLocked
                          : hasConfirmedDate
                            ? styles.userDateStatusConfirmed
                            : styles.userDateStatusPending;
                        const mainActionLabel = lockedByAnotherReservation
                          ? t("myReservations.alreadyValidatedForAnotherClient")
                          : hasConfirmedDate
                            ? t("myReservations.cancelReservationAction")
                            : t("myReservations.validateReservationAction");
                        const groupColor = group.reservationColor || group.dates.find((entry) => entry.reservationColor)?.reservationColor || RESERVATION_COLORS[0];

                        return (
                          <div key={group.id} className={styles.ownerReservationGroupCard}>
                            <div className={styles.ownerReservationDateTags}>
                              {group.dates.map((dateItem) => (
                                <span key={`${group.id}-${dateItem.date}`} className={styles.ownerReservationDateTag}>
                                  {dateItem.date}
                                </span>
                              ))}
                            </div>
                            <div className={styles.ownerReservationGroupControls}>
                              <div className={styles.ownerReservationColorPickerWrap}>
                                <button
                                  type="button"
                                  className={styles.ownerReservationColorButton}
                                  style={{ "--reservation-color": groupColor } as React.CSSProperties}
                                  onClick={() => setOpenColorGroupId((current) => current === group.id ? null : group.id)}
                                  title={t("myReservations.changeReservationColor")}
                                  aria-label={t("myReservations.changeReservationColor")}
                                  disabled={colorSavingGroupId === group.id}
                                />
                                {openColorGroupId === group.id && (
                                  <div className={styles.ownerReservationColorPalette}>
                                    {RESERVATION_COLORS.map((color) => (
                                      <button
                                        key={`${group.id}-${color}`}
                                        type="button"
                                        className={styles.ownerReservationColorSwatch}
                                        style={{ "--reservation-color": color } as React.CSSProperties}
                                        onClick={() => handleReservationColorChange(item, group, color)}
                                        aria-label={t("myReservations.selectReservationColor")}
                                        disabled={colorSavingGroupId === group.id}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className={styles.ownerReservationGroupActions}>
                                {isPastPendingGroup ? (
                                  <button
                                    type="button"
                                    className={`${styles.userDateStatus} ${styles.userDateStatusPast} ${styles.ownerReservationGroupAction}`}
                                    disabled
                                  >
                                    {t("myReservations.expiredReservationAction")}
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      className={`${styles.userDateStatus} ${actionClass} ${styles.ownerReservationGroupAction}`}
                                      onClick={() => requestGroupAction(item, group)}
                                      disabled={actionLoading || lockedByAnotherReservation}
                                      title={lockedByAnotherReservation ? t("myReservations.dateLockedByConfirmedReservation") : undefined}
                                    >
                                      {mainActionLabel}
                                    </button>
                                    {!hasConfirmedDate && !lockedByAnotherReservation && (
                                      <button
                                        type="button"
                                        className={`${styles.userDateStatus} ${styles.userDateStatusConfirmed} ${styles.ownerReservationGroupAction}`}
                                        onClick={() => requestGroupAction(item, group, "deletePending")}
                                        disabled={actionLoading}
                                      >
                                        {t("myReservations.rejectReservationAction")}
                                      </button>
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                            {group.stayReview ? (
                              <div className={styles.ownerReservationReview}>
                                <strong>{t("myReservations.stayReviewTitle")}</strong>
                                <span>{t("myReservations.stayReviewRating", { count: group.stayReview.rating })}</span>
                                <p>{group.stayReview.content}</p>
                                <small>
                                  {group.stayReview.valide
                                    ? t("myReservations.stayReviewValidated")
                                    : t("myReservations.stayReviewPending")}
                                </small>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </article>
              );
            })}
            <div ref={sentinelRef} className={styles.sentinel} />
            {loadingMore && <p className={styles.state}>{t("myReservations.loadingMoreReservations")}</p>}
          </section>
        )}
      </div>

      {confirmAction && (
        <div className={styles.popupOverlay} onClick={() => !actionLoading && setConfirmAction(null)}>
          <div className={styles.popupConfirm} onClick={(event) => event.stopPropagation()}>
            <h3>
              {confirmAction.mode === "validate"
                ? t("myReservations.confirmValidateReservation")
                : confirmAction.mode === "deletePending"
                  ? t("myReservations.confirmRejectReservation")
                  : t("myReservations.confirmCancelReservation")}
            </h3>
            <p className={styles.popupConfirmDate}>{confirmActionSummary}</p>
            <div className={styles.popupConfirmActions}>
              <button type="button" className={styles.popupConfirmYes} onClick={executeReservationAction} disabled={actionLoading}>
                {t("myReservations.yes")}
              </button>
              <button
                type="button"
                className={styles.popupConfirmNo}
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                {t("myReservations.no")}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingManualClientReservation && (
        <div className={styles.popupOverlay} onClick={closeManualClientEditPopup}>
          <div className={styles.manualClientEditModal} onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={styles.manualClientEditClose}
              onClick={closeManualClientEditPopup}
              disabled={manualClientEditSaving}
              aria-label={t("myReservations.closePopup")}
            >
              x
            </button>
            <h3>{t("myReservations.editManualClientTitle")}</h3>
            <p>{t("myReservations.editManualClientDescription")}</p>
            <form className={styles.manualClientEditForm} onSubmit={handleManualClientEditSubmit}>
              <div className={styles.manualClientEditGrid}>
                <div className={styles.manualClientEditField}>
                  <label htmlFor="manual-client-lastName">{t("adDetails.lastName")}</label>
                  <input
                    id="manual-client-lastName"
                    type="text"
                    name="lastName"
                    value={manualClientEditForm.lastName}
                    onChange={handleManualClientEditInputChange}
                    disabled={manualClientEditSaving}
                  />
                  {manualClientEditErrors.lastName && <small>{manualClientEditErrors.lastName}</small>}
                </div>

                <div className={styles.manualClientEditField}>
                  <label htmlFor="manual-client-firstName">{t("adDetails.firstName")}</label>
                  <input
                    id="manual-client-firstName"
                    type="text"
                    name="firstName"
                    value={manualClientEditForm.firstName}
                    onChange={handleManualClientEditInputChange}
                    disabled={manualClientEditSaving}
                  />
                  {manualClientEditErrors.firstName && <small>{manualClientEditErrors.firstName}</small>}
                </div>

                <div className={styles.manualClientEditField}>
                  <label htmlFor="manual-client-status">{t("adDetails.status")}</label>
                  <select
                    id="manual-client-status"
                    name="status"
                    value={manualClientEditForm.status}
                    onChange={handleManualClientEditInputChange}
                    disabled={manualClientEditSaving}
                  >
                    <option value="">{t("adDetails.statusPlaceholder")}</option>
                    <option value="single">{t("adDetails.statusSingle")}</option>
                    <option value="married">{t("adDetails.statusMarriedNoChildren")}</option>
                    <option value="married_with_children">{t("adDetails.statusMarriedWithChildren")}</option>
                  </select>
                  {manualClientEditErrors.status && <small>{manualClientEditErrors.status}</small>}
                </div>

                <div className={styles.manualClientEditField}>
                  <label htmlFor="manual-client-phone">{t("adDetails.phone")} ({t("adDetails.optional")})</label>
                  <PhoneInput onChange={handleManualClientEditInputChange} value={manualClientEditForm.phone} name="phone" />
                  {manualClientEditErrors.phone && <small>{manualClientEditErrors.phone}</small>}
                </div>

                {manualClientEditForm.status === "married_with_children" && (
                  <div className={styles.manualClientEditField}>
                    <label htmlFor="manual-client-nbr-child">{t("adDetails.nbrChild")}</label>
                    <select
                      id="manual-client-nbr-child"
                      name="nbr_child"
                      value={manualClientEditForm.nbr_child}
                      onChange={handleManualClientEditInputChange}
                      disabled={manualClientEditSaving}
                    >
                      <option value="">{t("adDetails.nbrChildPlaceholder")}</option>
                      {Array.from({ length: 8 }, (_, i) => i).map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                    {manualClientEditErrors.nbr_child && <small>{manualClientEditErrors.nbr_child}</small>}
                  </div>
                )}
              </div>

              <div className={styles.manualClientEditActions}>
                <button
                  type="button"
                  className={styles.manualClientEditCancel}
                  onClick={closeManualClientEditPopup}
                  disabled={manualClientEditSaving}
                >
                  {t("myReservations.editManualClientCancel")}
                </button>
                <button
                  type="submit"
                  className={styles.manualClientEditSubmit}
                  disabled={manualClientEditSaving}
                >
                  {manualClientEditSaving
                    ? t("myReservations.editManualClientSaving")
                    : t("myReservations.editManualClientSave")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReservationManagementPage;
